import React, { useEffect } from 'react';
import { Divider } from '@mui/material';
import { DeleteIcon } from '../../components/buttons';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams, GridColumnVisibilityModel } from '@mui/x-data-grid';
import Header from './components/header';
import { COUNTRY_NAMES } from '../../models/country';
import { formatDateTime } from '../../utils/misc';
import { useTranslation } from 'react-i18next';
import { useReservations as useReservation } from '../../hooks';
import { Address, Vehicle } from '../../models/vehicle';

const ReservationPage: React.FC = () => {
  const { t } = useTranslation();
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState<GridColumnVisibilityModel>({ id: false, vehicle_id: false, loadingPlaces: false });

  const {
    reservations,
    query,
    loading,
    DeleteModal,
    rowCount,
    paginationModel,
    Dialog,
    fetchReservations,
    setIsOpen,
    setFormData,
    setAction,
    handleDelete,
    setPaginationModel,
  } = useReservation();

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations, query, paginationModel]);

  const handleRowClick = (params: GridRowParams) => {
    const reservation = reservations.find(item => item.id === params.id);
    if (reservation) {
      setAction('view');
      setFormData(reservation);
      setIsOpen(true);
    }
  };

  //FIXME: Should use Partial<Driver>. But why in Driver we got everything in snake_case instead of camelCase like on BE??!
  const renderDriver = (driver: { firstName: string; lastName: string }) => (
    driver ? `${driver.firstName} ${driver.lastName}` : ''
  );

  const renderVehicle = (vehicle: Partial<Vehicle>) => (
    vehicle ? `${vehicle.truckPlate}${vehicle.trailerPlate ? ' / ' + vehicle.trailerPlate : ''}` : ''
  );

  const renderActionCell = ({ id }: GridRenderCellParams) => (
    <DeleteIcon onClick={e => { e.stopPropagation(); handleDelete(id); }} />
  );

  const renderPickupLocation = (pickupLocation: Address) => (
    pickupLocation ? `${pickupLocation?.city}, ${COUNTRY_NAMES[pickupLocation.country]}` : ''
  );

  const columns: GridColDef[] = [
    { field: 'id', headerName: t('ID'), width: 50 },
    { field: 'driver', headerName: t('Driver'), flex: 1, renderCell: ({ value }) => renderDriver(value) },
    { field: 'vehicle', headerName: t('Vehicle'), flex: 1, renderCell: ({ value }) => renderVehicle(value) },
    { field: 'pickupLocation', headerName: t('Pickup location'), flex: 1, renderCell: ({ value }) => renderPickupLocation(value) },
    { field: 'dropoffLocation', headerName: t('Dropoff location'), flex: 1, renderCell: ({ value }) => renderPickupLocation(value) },
    { field: 'startTime', headerName: t('Start time'), width: 150, renderCell: ({ value }) => value ? formatDateTime(value) : '' },
    { field: 'endTime', headerName: t('End time'), width: 150, renderCell: ({ value }) => value ? formatDateTime(value) : '' },
    { field: 'updatedOn', headerName: t('Updated on'), width: 200, renderCell: ({ value }) => value ? formatDateTime(value) : '' },
    { field: 'actions', headerName: t('Actions'), width: 150, renderCell: renderActionCell }
  ];

  return (
    <>
      <Header
        setIsOpen={setIsOpen}
        rowCount={rowCount}
      />

      <Divider />

      <DataGrid
        rows={reservations}
        columns={columns}
        onRowClick={params => handleRowClick(params)}
        pagination
        paginationMode='server'
        disableRowSelectionOnClick
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        rowCount={rowCount}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(model) => setColumnVisibilityModel(model)}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={loading}
        localeText={{
          noRowsLabel: t('No reservations found'),
          noResultsOverlayLabel: t('No reservations found'),
        }}
      />

      {/* modal */}
      {Dialog}
      {DeleteModal}
    </>
  );
};

export default ReservationPage;
